import { Component } from 'react';
import AnnonceItem from '../../components/AnnonceItem';
import { DAY_LABELS } from '../../constants/vehicleOptions';

const HOURS = Array.from(
  { length: 48 },
  (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`
);
const QUALITY = { sombre: 'trop sombre', floue: 'floue', cadrage: 'mal cadrée' };
const noop = () => {};

// Un défaut dans la carte ne doit jamais empêcher de publier
class PreviewBoundary extends Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error('Aperçu annonce', error);
  }

  render() {
    return this.state.failed ? (
      <p className="mc-field-hint">L'aperçu n'a pas pu s'afficher, mais vous pouvez publier.</p>
    ) : (
      this.props.children
    );
  }
}

// `inert` posé via ref : fonctionne en React 18 comme en React 19
const makeInert = (el) => {
  if (el) el.setAttribute('inert', '');
};

function StepReview({ state, patch, busy, onBack, onPublish }) {
  const { preview, schedule } = state;
  const suggestion = preview?.price;

  const setSchedule = (p) => patch((s) => ({ schedule: { ...s.schedule, ...p } }));
  const toggleDay = (d) =>
    patch((s) => ({
      schedule: {
        ...s.schedule,
        days: s.schedule.days.includes(d)
          ? s.schedule.days.filter((x) => x !== d)
          : [...s.schedule.days, d].sort((a, b) => a - b),
      },
    }));

  const warnings = (state.analysis?.photos?.photos || [])
    .flatMap((p) => [
      p.quality !== 'ok' && `Photo ${p.index + 1} : ${QUALITY[p.quality] || 'qualité à vérifier'}`,
      p.plateVisible && `Photo ${p.index + 1} : plaque d'immatriculation visible`,
    ])
    .filter(Boolean);

  const cover = state.coverUrl;
  const urls = state.photos.filter((p) => p.url).map((p) => p.url);
  const images = (cover ? [cover, ...urls.filter((u) => u !== cover)] : urls).join(',');

  const previewAnnonce = {
    vehiculeAnnonceId: 0,
    reservationPrice: Number(state.price) || 0,
    Vehicle: { images, VehicleModel: state.modele || {} },
    distance: 0,
    nombreJours: 1,
    rating: null,
    reviewsCount: 0,
    isInstant: true,
    isUnavailable: false,
  };

  const canPublish =
    Number(state.price) >= 5 &&
    state.description.trim().length >= 20 &&
    schedule.days.length > 0 &&
    schedule.from < schedule.until;

  return (
    <>
      <section className="mc-form-card">
        <span className="mc-section-kicker">APERÇU</span>
        <h1 className="mc-form-title">Voici votre annonce</h1>
        <p className="mc-form-subtitle">Tout est modifiable. Rien n'est publié avant votre clic.</p>

        {/* Aperçu inerte : mêmes composants que la recherche */}
        <div className="mv-express-preview" ref={makeInert} aria-hidden="true">
          <PreviewBoundary>
            <AnnonceItem
              propsData={previewAnnonce}
              searchStart={null}
              searchEnd={null}
              currentUserId={null}
              isSelected={false}
              isFavorite={false}
              onToggleSelect={noop}
              onToggleFavorite={noop}
            />
          </PreviewBoundary>
        </div>

        {warnings.length > 0 && (
          <div className="mc-form-alert" role="note">
            <ion-icon name="alert-circle-outline"></ion-icon>
            <span>{warnings.join(' · ')}. Vous pouvez revenir à l'étape 1 pour les remplacer.</span>
          </div>
        )}
      </section>

      <section className="mc-form-card">
        <span className="mc-section-kicker">PRIX</span>
        <h2 className="mc-form-title">Prix par jour</h2>
        <p className="mc-field-hint">
          {suggestion
            ? `Les voitures comparables se louent entre ${suggestion.low} et ${suggestion.high} € par jour (${suggestion.sample} annonces ${suggestion.scope === 'brand' ? 'de la même marque' : 'dans votre zone'}).`
            : state.fallbackPrice
              ? `Estimation Moovicar : environ ${state.fallbackPrice} € par jour.`
              : 'Indiquez le prix par jour que vous souhaitez demander.'}
        </p>

        {suggestion && (
          <div className="mv-express-chips">
            {[
              ['Économique', suggestion.low],
              ['Conseillé', suggestion.median],
              ['Premium', suggestion.high],
            ].map(([label, value]) => (
              <button
                key={label}
                type="button"
                className="mv-express-chip"
                onClick={() => patch({ price: String(value) })}
              >
                {label} · {value} €
              </button>
            ))}
          </div>
        )}

        <div className="mc-input-affix mc-input-affix--wide">
          <input
            type="number"
            min="5"
            step="1"
            className="mc-input"
            value={state.price}
            onChange={(e) => patch({ price: e.target.value })}
            aria-label="Prix par jour"
          />
          <span>€ / jour</span>
        </div>
      </section>

      <section className="mc-form-card">
        <span className="mc-section-kicker">DISPONIBILITÉS</span>
        <h2 className="mc-form-title">Quand pouvez-vous remettre les clés ?</h2>

        <div className="mv-express-chips">
          {DAY_LABELS.map((label, d) => (
            <button
              key={label}
              type="button"
              className={`mv-express-chip${schedule.days.includes(d) ? ' is-active' : ''}`}
              aria-pressed={schedule.days.includes(d)}
              onClick={() => toggleDay(d)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mc-grid-2 mc-grid-2--tight mv-express-fields">
          <select
            className="mc-select"
            value={schedule.from}
            onChange={(e) => setSchedule({ from: e.target.value })}
            aria-label="Début"
          >
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
          <select
            className="mc-select"
            value={schedule.until}
            onChange={(e) => setSchedule({ until: e.target.value })}
            aria-label="Fin"
          >
            {HOURS.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </div>
        {schedule.from >= schedule.until && (
          <p className="mc-field-error" role="alert">
            L'heure de fin doit être après l'heure de début.
          </p>
        )}
        <p className="mc-field-hint">Vous pourrez affiner jour par jour depuis votre compte.</p>
      </section>

      <section className="mc-form-card">
        <span className="mc-section-kicker">DESCRIPTION</span>
        <textarea
          className="mc-input"
          rows={7}
          maxLength={2000}
          value={state.description}
          onChange={(e) => patch({ description: e.target.value })}
          aria-label="Description"
        />
        <p className="mc-field-hint">
          {preview?.source === 'template'
            ? 'Texte simple généré sans IA : vous pouvez le compléter. '
            : ''}
          Relisez : vous restez responsable de l'exactitude de votre annonce.
        </p>

        <div className="mc-form-actions">
          <button
            type="button"
            className="mc-btn mc-btn--secondary"
            onClick={onBack}
            disabled={busy}
          >
            <ion-icon name="arrow-back-outline"></ion-icon>
            Précédent
          </button>
          <button
            type="button"
            className="mc-btn mc-btn--primary"
            onClick={onPublish}
            disabled={!canPublish || busy}
          >
            {busy ? 'Publication…' : 'Publier mon annonce'}
            <ion-icon name="checkmark-outline"></ion-icon>
          </button>
        </div>
      </section>
    </>
  );
}

export default StepReview;
